import { itemTypeSettingKey } from "../../../OverviewModal/resolveItemTypeUnit";
import type { PriceProperty } from "../../../OverviewModal/useItemTypeSettings";
import type {
  ExportGroupValues,
  GroupValuesOf,
} from "../../buildExportGroupValues";
import type { BuildGroupCells, CountListValueRow } from "./types";

interface GroupLine {
  name: string;
  values: ExportGroupValues | null;
}

const groupLinesOf = (
  row: CountListValueRow,
  groupValuesOf: GroupValuesOf,
): GroupLine[] => {
  // Diagram rows have no settings behind them
  if (row.kind === "unmatched") {
    return row.source === "diagram"
      ? [{ name: row.name, values: null }]
      : [
          {
            name: row.name,
            values: groupValuesOf({ kind: row.source, name: row.name }),
          },
        ];
  }

  // The same group counted on several plans is still one line
  const seenKeys = new Set<string>();
  const lines: GroupLine[] = [];
  for (const { kind, name } of row.detectedTypes) {
    const key = itemTypeSettingKey(kind, name);
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    lines.push({ name, values: groupValuesOf({ kind, name }) });
  }
  return lines;
};

export const buildGroupCells: BuildGroupCells = ({
  row,
  groupValuesOf,
  formatPriceValue,
}) => {
  const formatOrEmpty = (
    value: number | null | undefined,
    property: PriceProperty,
  ): string =>
    value === null || value === undefined
      ? ""
      : formatPriceValue({ property, value });

  const lines = groupLinesOf(row, groupValuesOf);

  // A position without any group still gets its one row, with the unit the row builder gave it
  if (lines.length === 0) {
    return [
      {
        detectedType: "",
        unit: row.unit,
        materialPrice: "",
        hours: "",
        hourlyRate: "",
        unitPrice: "",
      },
    ];
  }

  return lines.map((line) => ({
    detectedType: line.name,
    unit: line.values?.unit ?? row.unit,
    materialPrice: formatOrEmpty(line.values?.materialPrice, "amount"),
    hours: formatOrEmpty(line.values?.hours, "hours"),
    hourlyRate: formatOrEmpty(line.values?.hourlyRate, "hourlyRate"),
    unitPrice: formatOrEmpty(line.values?.unitPrice, "amount"),
  }));
};
